import { ReactElement, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";

import Debug from "debug";
import { Loader } from "@screencloud/screencloud-ui-components";
import { ScreenCloud, ScreenCloudContext } from "../../ScreenCloudReactApp";
import { AppConfig, EditorStore, Stream, TokenStore } from "../../types";
import "./Setup.scss";

import StreamAuthView from "../../components/StreamAuthView/StreamAuthView";
import StreamCreateView from "../../components/StreamCreateView/StreamCreateView";
import ErrorMessage from "../../components/ErrorMessage/ErrorMessage";

import {
  getChannels,
  getChannel,
  configChannelPresent,
  requestStudioToken,
} from "../../actions";
import { RootState } from "../../reducers";
import TokenError from "../../components/TokenError/TokenError";
import { useContext } from "react";

const debug = Debug("broadcast:Setup");

interface Props {
  sc: ScreenCloud;
}

const Setup = (props: Props): ReactElement<Props> => {
  const { sc } = props;
  const {
    config,
    orgId,
    spaceId = "",
    emitConfigUpdateAvailable,
    onRequestConfigUpdate,
    requestAuthToken,
  } = sc;

  const studioToken: TokenStore = useSelector(
    (state: RootState) => state.studio
  );
  const editor: EditorStore = useSelector((state: RootState) => state.editor);
  const { logger } = useContext(ScreenCloudContext);

  const channelRef = useRef<Stream>(editor.channel as Stream);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(requestStudioToken({ requestAuthToken }));
  }, [dispatch, requestAuthToken]);

  useEffect(() => {
    debug("token updated");
    const { token } = studioToken;
    if (token !== null) {
      dispatch(getChannels({ orgId, spaceId }, logger));
      debug("config", config);
      if (config && config.channelId) {
        const { channelId } = config;
        dispatch(getChannel({ orgId, spaceId, channelId }));
      } else {
        // let's editor store know that loading state shouldn't wait for getChannel response
        dispatch(configChannelPresent(true));
      }
    }
  }, [studioToken, config, dispatch, orgId, spaceId, logger]);

  useEffect(() => {
    if (editor.channel === null) {
      debug("Channel not created (or selected) yet.");
      return;
    }

    if (editor.channel !== null && channelRef.current !== editor.channel) {
      channelRef.current = editor.channel;

      const saveConfig = (): Promise<{
        config: AppConfig;
      }> => {
        return Promise.resolve({
          config: {
            channelId: channelRef.current.channelId,
          },
        });
      };

      debug("will update", editor.channel.channelId);
      onRequestConfigUpdate(saveConfig);
      emitConfigUpdateAvailable();
    }
  }, [editor, emitConfigUpdateAvailable, onRequestConfigUpdate]);

  if (editor.loading) {
    return (
      <div className="loader-container">
        <Loader active size="massive" inline="centered" />
      </div>
    );
  }
  return (
    <div className="setup-wrapper">
      <div className="setup-error">
        {editor.errors.length > 0 &&
          editor.errors.map((error) => (
            <ErrorMessage key={error.uuid} error={error} />
          ))}
      </div>
      {studioToken.token === null ? (
        <TokenError />
      ) : editor.channel ? (
        <StreamAuthView />
      ) : (
        <StreamCreateView orgId={orgId} spaceId={spaceId} />
      )}
    </div>
  );
};

export default Setup;
